/*
 * Presentación de las filas de /attendance/jornadas/ y /attendance/horas/ (contrato de la etapa 1).
 *
 * No recalcula reglas de negocio: puntualidad, atraso, adelanto, horas y almuerzo vienen del servidor
 * (spec 4.2 a 4.4). Aquí solo se filtra, ordena, resume y da formato para las secciones Ingresos,
 * Almuerzos, Salidas y Horas trabajadas. Funciones puras (se prueban en rows.test.js).
 *
 * Fila de jornada: id_empleado, empleado, cargo, id_tienda, tienda, id_zona, zona, fecha, estado
 * ('completa' | 'incompleta' | 'en_curso'), entrada, inicio_almuerzo, fin_almuerzo, salida ('HH:MM:SS' o
 * null), minutos_trabajados, horas, hora_oficial, limite_puntual, puntual, atraso_min, adelanto_min,
 * llegada_temprana, almuerzo_min, almuerzo_excedido, sin_almuerzo.
 * Fila de horas: id_empleado, empleado, cargo, id_tienda, tienda, id_zona, zona, periodo_inicio,
 * periodo_fin, minutos_trabajados, horas, jornadas_completas, jornadas_incompletas, jornadas_en_curso,
 * jornadas_sin_almuerzo.
 */
export const PAGE_SIZE = 20;

const pad = (value) => String(value).padStart(2, '0');
const isNumber = (value) => typeof value === 'number' && !Number.isNaN(value);

/** Número con coma decimal ('8,5'); '—' si no hay dato. */
export function formatNumber(value, decimals = 1) {
    if (!isNumber(value)) return '—';
    return value.toFixed(decimals).replace('.', ',');
}

/** 'HH:MM:SS' -> 'HH:MM'; '—' si no hay marca. */
export function hhmm(value) {
    return value ? value.slice(0, 5) : '—';
}

/** 'HH:MM[:SS]' -> minutos desde la medianoche (o null). */
export function toMinutes(value) {
    if (!value) return null;
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
}

/** Minutos desde la medianoche -> 'HH:MM' (redondeado); '—' si no hay dato. */
export function minutesToClock(minutes) {
    if (!isNumber(minutes)) return '—';
    const rounded = Math.round(minutes);
    return `${pad(Math.floor(rounded / 60))}:${pad(rounded % 60)}`;
}

/** Duración legible: '45 min', '2 h', '1 h 10 min'. */
export function durationLabel(minutes) {
    if (!isNumber(minutes)) return '—';
    const rounded = Math.round(minutes);
    const hours = Math.floor(rounded / 60);
    const rest = rounded % 60;
    if (hours === 0) return `${rest} min`;
    if (rest === 0) return `${hours} h`;
    return `${hours} h ${rest} min`;
}

/** Porcentaje con 1 decimal, o null si el total es 0. */
export function share(part, total) {
    return total ? Math.round((1000 * part) / total) / 10 : null;
}

function average(values) {
    const numbers = values.filter(isNumber);
    return numbers.length ? numbers.reduce((sum, value) => sum + value, 0) / numbers.length : null;
}

const hasValue = (value) => value !== null && value !== undefined;

/** Filtra por zona, tienda y empleado (ids como texto, igual que en la URL). */
export function applyEntityFilters(rows, filters) {
    const same = (value, wanted) => !wanted || String(value) === String(wanted);
    return rows.filter((row) => same(row.id_zona, filters.zona) && same(row.id_tienda, filters.tienda)
        && same(row.id_empleado, filters.empleado));
}

function collect(rows, idKey, nameKey) {
    const found = new Map();
    rows.forEach((row) => {
        if (hasValue(row[idKey])) found.set(String(row[idKey]), row[nameKey]);
    });
    return [...found.entries()]
        .map(([id, nombre]) => ({id, nombre}))
        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

/**
 * Opciones de los filtros sacadas de las jornadas del periodo: las tiendas se limitan a la zona
 * elegida y los empleados a la zona y tienda elegidas.
 */
export function filterOptions(rows, filters) {
    const inZone = applyEntityFilters(rows, {zona: filters.zona});
    const inStore = applyEntityFilters(rows, {zona: filters.zona, tienda: filters.tienda});
    return {
        zonas: collect(rows, 'id_zona', 'zona'),
        tiendas: collect(inZone, 'id_tienda', 'tienda'),
        empleados: collect(inStore, 'id_empleado', 'empleado'),
    };
}

/**
 * Estado de la llegada para la insignia de la fila. tone: 'up' (antes de la hora oficial), 'neutral'
 * (a tiempo, dentro de la tolerancia), 'down' (atraso) o 'muted' (sin entrada o día sin hora oficial).
 */
export function arrivalStatus(row) {
    if (!row.entrada) return {tone: 'muted', text: 'Sin entrada'};
    if (!hasValue(row.puntual)) return {tone: 'muted', text: 'Sin hora oficial'};
    if (row.llegada_temprana) return {tone: 'up', text: `${row.adelanto_min} min antes`};
    if (row.puntual) return {tone: 'neutral', text: 'A tiempo'};
    return {tone: 'down', text: `${row.atraso_min} min tarde`};
}

function byDateDescThen(key) {
    return (a, b) => {
        if (a.fecha !== b.fecha) return a.fecha < b.fecha ? 1 : -1;
        return String(a[key]).localeCompare(String(b[key])) || a.empleado.localeCompare(b.empleado, 'es');
    };
}

/** Ingresos: resumen del periodo. */
export function entrySummary(rows) {
    const withEntry = rows.filter((row) => row.entrada);
    const evaluated = withEntry.filter((row) => hasValue(row.puntual));
    const late = evaluated.filter((row) => row.puntual === false);
    return {
        llegadas: withEntry.length,
        evaluadas: evaluated.length,
        puntualidad: share(evaluated.filter((row) => row.puntual).length, evaluated.length),
        temprano: evaluated.filter((row) => row.llegada_temprana).length,
        atrasos: late.length,
        atrasoTotal: late.reduce((sum, row) => sum + row.atraso_min, 0),
        horaPromedio: minutesToClock(average(withEntry.map((row) => toMinutes(row.entrada)))),
    };
}

/** Ingresos: jornadas con entrada, del día más reciente al más antiguo y, en el día, por hora de llegada. */
export function entryList(rows) {
    return rows.filter((row) => row.entrada).sort(byDateDescThen('entrada'));
}

/** Almuerzos: lista de la marca elegida ('inicio' o 'fin'). */
export function lunchList(rows, mark) {
    const key = mark === 'fin' ? 'fin_almuerzo' : 'inicio_almuerzo';
    return rows.filter((row) => row[key]).sort(byDateDescThen(key));
}

/** Almuerzos: insignia de la fila según la marca que se está viendo ('inicio' o 'fin'). */
export function lunchBadge(row, mark) {
    if (mark === 'fin') {
        if (!hasValue(row.almuerzo_min)) return {tone: 'down', text: 'Sin inicio marcado'};
        if (row.almuerzo_excedido) return {tone: 'down', text: `${durationLabel(row.almuerzo_min)} · excedido`};
        return {tone: 'neutral', text: durationLabel(row.almuerzo_min)};
    }
    if (row.fin_almuerzo) return {tone: 'neutral', text: `Regresó ${hhmm(row.fin_almuerzo)}`};
    if (row.estado === 'en_curso') return {tone: 'muted', text: 'En almuerzo'};
    return {tone: 'down', text: 'Sin regreso marcado'};
}

/** Almuerzos: resumen del periodo. */
export function lunchSummary(rows) {
    const withDuration = rows.filter((row) => hasValue(row.almuerzo_min));
    const averageMinutes = average(withDuration.map((row) => row.almuerzo_min));
    return {
        inicios: rows.filter((row) => row.inicio_almuerzo).length,
        fines: rows.filter((row) => row.fin_almuerzo).length,
        enAlmuerzo: rows.filter((row) => row.estado === 'en_curso' && row.inicio_almuerzo && !row.fin_almuerzo).length,
        promedio: averageMinutes === null ? null : Math.round(averageMinutes),
        excedidos: withDuration.filter((row) => row.almuerzo_excedido).length,
        sinAlmuerzo: rows.filter((row) => row.sin_almuerzo).length,
    };
}

/** Almuerzos: promedio por persona, de quien demora menos a quien demora más. */
export function lunchByPerson(rows) {
    const people = new Map();
    rows.forEach((row) => {
        if (!hasValue(row.almuerzo_min)) return;
        const person = people.get(row.id_empleado)
            || {id_empleado: row.id_empleado, empleado: row.empleado, tienda: row.tienda, zona: row.zona, dias: 0, total: 0, excedidos: 0};
        person.dias += 1;
        person.total += row.almuerzo_min;
        if (row.almuerzo_excedido) person.excedidos += 1;
        people.set(row.id_empleado, person);
    });
    return [...people.values()]
        .map(({total, ...person}) => ({...person, promedio: Math.round(total / person.dias)}))
        .sort((a, b) => a.promedio - b.promedio || a.empleado.localeCompare(b.empleado, 'es'));
}

/** Salidas: jornadas con salida marcada. */
export function exitList(rows) {
    return rows.filter((row) => row.salida).sort(byDateDescThen('salida'));
}

/** Salidas: jornadas con entrada pero sin salida (en curso hoy o incompletas de días pasados). */
export function pendingExits(rows) {
    return rows.filter((row) => row.entrada && !row.salida).sort(byDateDescThen('entrada'));
}

/** Salidas: insignia de la fila (horas del día, jornada en curso o salida faltante). */
export function exitBadge(row) {
    if (row.salida) {
        if (!row.entrada) return {tone: 'down', text: 'Salió sin entrada · horas: —'};
        if (row.estado === 'completa') {
            // sin_almuerzo lo entrega el servidor solo en jornadas completas: se usa tal cual.
            const worked = `${durationLabel(row.minutos_trabajados)} trabajadas`;
            if (row.sin_almuerzo === true) return {tone: 'down', text: `Sin almuerzo · ${worked}`};
            return {tone: 'neutral', text: worked};
        }
        return {tone: 'down', text: 'Jornada incompleta'};
    }
    if (row.estado === 'en_curso') return {tone: 'muted', text: 'En curso'};
    return {tone: 'down', text: 'Falta la salida'};
}

/** Salidas: resumen del periodo. */
export function exitSummary(rows) {
    const withExit = rows.filter((row) => row.salida);
    return {
        salidas: withExit.length,
        sinSalida: rows.filter((row) => row.estado === 'incompleta' && row.entrada && !row.salida).length,
        enCurso: rows.filter((row) => row.estado === 'en_curso').length,
        sinAlmuerzo: withExit.filter((row) => row.entrada && row.sin_almuerzo === true).length,
        sinEntrada: withExit.filter((row) => !row.entrada).length,
        horaPromedio: minutesToClock(average(withExit.map((row) => toMinutes(row.salida)))),
    };
}

/** Horas trabajadas: totales de las filas de /attendance/horas/. */
export function hoursSummary(hourRows) {
    const minutes = hourRows.reduce((sum, row) => sum + row.minutos_trabajados, 0);
    const people = new Set(hourRows.filter((row) => row.minutos_trabajados > 0).map((row) => row.id_empleado)).size;
    const total = (key) => hourRows.reduce((sum, row) => sum + row[key], 0);
    return {
        horas: Math.round(minutes / 6) / 10,
        personas: people,
        porPersona: people ? Math.round(minutes / people / 6) / 10 : null,
        completas: total('jornadas_completas'),
        incompletas: total('jornadas_incompletas'),
        sinAlmuerzo: total('jornadas_sin_almuerzo'),
    };
}

/** Horas trabajadas: periodo más reciente primero y, dentro del periodo, quien más horas tiene. */
export function hoursList(hourRows) {
    return [...hourRows].sort((a, b) => {
        if (a.periodo_inicio !== b.periodo_inicio) return a.periodo_inicio < b.periodo_inicio ? 1 : -1;
        return b.minutos_trabajados - a.minutos_trabajados || a.empleado.localeCompare(b.empleado, 'es');
    });
}

/** Página `page` (desde 1) de una lista; corrige páginas fuera de rango. */
export function paginate(list, page, size = PAGE_SIZE) {
    const pages = Math.max(1, Math.ceil(list.length / size));
    const current = Math.min(Math.max(1, page), pages);
    return {items: list.slice((current - 1) * size, current * size), page: current, pages};
}
